import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ArrowRight,
  Bot,
  Sparkles,
  CheckCircle2,
  AlertTriangle,
  Search,
  PhoneCall,
  ClipboardCheck,
  MapPin,
  Phone,
  Compass,
  ShieldCheck,
} from "lucide-react";
import { SiteLayout } from "@/components/SiteLayout";
import { PageHero } from "@/components/PageHero";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { buildFaqPageJsonLd } from "@/lib/serviceSchema";
import { ogMetaTags, twitterTextTags } from "@/lib/ogImages";
import heroImg from "@/assets/services/ai-visibility-audit-hero.jpg";

const SITE_URL = "https://coloradowebimpressions.com";
const PATH = "/ai-visibility-audit";
const OG_IMAGE = `${SITE_URL}${heroImg}`;

const FAQS = [
  {
    q: "How is the AI Visibility Audit different from your GEO & AI Search service?",
    a: "The audit is a one-time, fixed-fee deliverable: I personally run 25+ real customer prompts across ChatGPT, Perplexity, Gemini, and Google AI Overviews for your business, show you exactly where you're invisible, and hand you a 90-day roadmap. The GEO & AI Search service is the done-for-you monthly retainer where my team executes that roadmap. Most clients start with the audit, then decide if they want me coaching them or doing the work for them.",
  },
  {
    q: "Do I need this if I already rank well on Google?",
    a: "Probably yes. AI Overviews are already cutting clicks to top-ranked pages by 30%+ — what the industry calls 'The Great Decoupling.' Your impressions go up while clicks go down, because the AI answer at the top of the page is satisfying the search without anyone clicking through. Ranking #1 in 2026 is not the same thing as being chosen by AI tools. The audit shows you exactly what's happening.",
  },
  {
    q: "What if AI never names my Colorado business?",
    a: "Then you have a clear, prioritized fix list — schema upgrades, citation gaps, content rewrites, GBP work — to change that over the next 90 days. The audit's whole purpose is to surface what's broken. If we run the prompts and you're already getting cited everywhere, I'll tell you, refund the difference if your situation is simpler than the audit assumes, and recommend something else.",
  },
  {
    q: "What's included in the Coach + Audit tier?",
    a: "Everything in the audit, plus three monthly 1:1 strategy calls with me (Chris) and Slack/email access between calls for 90 days. This is for owners who want a trusted Colorado guide walking them through AI search, GEO, and digital marketing decisions — not a vendor who hands them a report and disappears.",
  },
  {
    q: "How long does the audit take?",
    a: "Seven business days from when you submit your business details. You get the full report (PDF + walkthrough Loom) plus a 60-minute live call with me to go through it.",
  },
  {
    q: "Can you guarantee I'll get cited by ChatGPT?",
    a: "No — and run from anyone who does. Nobody can guarantee placement inside an LLM. What I can guarantee is a clear, honest picture of where you stand today, the specific structural changes that move the needle, and 25+ years of judgment about which fixes are worth your time.",
  },
];

const SERVICE_JSON_LD = {
  "@context": "https://schema.org",
  "@type": "Service",
  name: "AI Visibility Audit & Coaching",
  serviceType: "AI Search Visibility Audit",
  provider: {
    "@type": "LocalBusiness",
    name: "Colorado Web Impressions",
    url: SITE_URL,
    areaServed: { "@type": "State", name: "Colorado" },
  },
  areaServed: "Colorado",
  description:
    "A 7-day audit and 90-day roadmap that shows Colorado small business owners exactly where they stand inside ChatGPT, Perplexity, Gemini, and Google AI Overviews — with optional 1:1 coaching from Chris Heidlebaugh.",
  url: `${SITE_URL}${PATH}`,
  offers: [
    {
      "@type": "Offer",
      name: "AI Visibility Audit",
      price: "750",
      priceCurrency: "USD",
      description:
        "One-time AI search audit, citation gap report, 90-day roadmap, and 60-minute strategy call.",
    },
    {
      "@type": "Offer",
      name: "Coach + Audit",
      price: "1850",
      priceCurrency: "USD",
      description:
        "Audit plus three monthly 1:1 strategy calls with Chris and Slack/email access for 90 days.",
    },
  ],
};

const BREADCRUMB_JSON_LD = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
    { "@type": "ListItem", position: 2, name: "AI Visibility Audit & Coaching", item: `${SITE_URL}${PATH}` },
  ],
};

export const Route = createFileRoute("/ai-visibility-audit")({
  head: () => ({
    meta: [
        { name: "description", content: "Find out if ChatGPT, Perplexity, and Google AI Overviews recommend your Colorado business. Free AI visibility audit by Chris Heidlebaugh." },
      { title: "AI Visibility Audit for Colorado Businesses | CWI" },
      {
        name: "description",
        content:
          "Are you invisible in ChatGPT? In 7 days I'll show you exactly where your Colorado business stands in AI search — and give you a 90-day fix roadmap. Optional 1:1 coaching available.",
      },
      { property: "og:title", content: "AI Visibility Audit for Colorado Businesses | CWI" },
      {
        property: "og:description",
        content:
          "A 7-day AI search audit + 90-day roadmap from a 25-year Colorado web pro. Optional monthly coaching for owners who want a trusted guide, not a vendor.",
      },
      { property: "og:url", content: `${SITE_URL}${PATH}` },
      { property: "og:type", content: "website" },
      ...ogMetaTags(OG_IMAGE),
      ...twitterTextTags({
        title: "AI Visibility Audit for Colorado Small Businesses",
        description: "7 days. Real prompts in ChatGPT, Perplexity, Gemini, AI Overviews. A 90-day roadmap. Optional coaching with Chris.",
      }),
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}${PATH}` }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(SERVICE_JSON_LD) },
      { type: "application/ld+json", children: JSON.stringify(BREADCRUMB_JSON_LD) },
      { type: "application/ld+json", children: JSON.stringify(buildFaqPageJsonLd(FAQS)) },
    ],
  }),
  component: AiVisibilityAuditPage,
});

function AiVisibilityAuditPage() {
  return (
    <SiteLayout>
      <PageHero
        eyebrow="AI Visibility Audit & Coaching"
        h1Text="AI Visibility Audit for Colorado Small Businesses"
        title={
          <>
            Are you <span className="text-brand-gold">invisible</span> in ChatGPT? Find out in 7 days.
          </>
        }
        description="I'll run 25+ real customer prompts across ChatGPT, Perplexity, Gemini, and Google AI Overviews for your Colorado business — then walk you through a 90-day roadmap to fix it."
        breadcrumbs={[
          { label: "Home", to: "/" },
          { label: "AI Visibility Audit" },
        ]}
        image={{
          src: heroImg,
          alt: "Colorado small business owner reviewing AI search results on a laptop",
        }}
      >
        <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <Link
            to="/contact"
            search={{ topic: "ai-audit" }}
            className="inline-flex items-center gap-2 rounded-full bg-brand-gold px-7 py-3 text-base font-semibold text-brand-black shadow-elegant transition-smooth hover:bg-brand-gold-deep"
          >
            Book my audit <ArrowRight className="h-4 w-4" />
          </Link>
          <a
            href="tel:+17194193935"
            className="inline-flex items-center gap-2 rounded-full border border-brand-cream/30 px-7 py-3 text-base font-semibold text-brand-cream transition-smooth hover:border-brand-gold hover:text-brand-gold"
           aria-label="Call (719) 419-3935">
            <Phone className="h-4 w-4" /> Talk to Chris first
          </a>
        </div>
      </PageHero>

      {/* Chris intro / first-person context */}
      <section className="bg-background py-16">
        <div className="mx-auto max-w-3xl px-5 lg:px-8">
          <p className="text-sm font-semibold uppercase tracking-wide text-brand-gold-deep">From Chris</p>
          <h2 className="mt-2 text-3xl font-bold text-foreground sm:text-4xl">
            Most Colorado business owners I talk to right now are completely lost about AI search. That's the whole reason I built this.
          </h2>
          <div className="mt-6 space-y-4 text-lg leading-relaxed text-muted-foreground">
            <p>
              I've been doing this in Colorado for 25+ years. The honest truth: SEO didn't die — but the rules just changed underneath
              everyone. AI Overviews are already cutting clicks to #1 results by more than 30%. ChatGPT, Perplexity, and Gemini are
              answering customer questions without ever sending the click. And nobody is sitting their plumber, dentist, or contractor
              down to explain what any of this means for their actual business.
            </p>
            <p>
              That's what this audit is. Not a sales pitch. Not a 60-page automated PDF. Me, personally, running the same questions your
              customers are asking — and showing you who's getting picked instead of you, why, and exactly what to do about it.
            </p>
          </div>
        </div>
      </section>

      {/* What you get */}
      <section className="border-t border-b border-border bg-section-alt py-20">
        <div className="mx-auto max-w-6xl px-5 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <p className="text-sm font-semibold uppercase tracking-wide text-brand-gold-deep">What you get</p>
            <h2 className="mt-2 text-3xl font-bold text-foreground sm:text-4xl">
              A real, productized deliverable — not vague consulting
            </h2>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-2">
            {[
              {
                icon: Search,
                title: "Live AI search audit",
                desc: "I run 25+ real customer prompts a buyer in your industry would actually type into ChatGPT, Perplexity, Gemini, and Google's AI Overviews. You see the screenshots. You see who's getting cited.",
              },
              {
                icon: AlertTriangle,
                title: "Citation gap report",
                desc: "Which competitors are getting named instead of you, why the LLMs trust them, and the specific structural reasons your site isn't getting picked up.",
              },
              {
                icon: ClipboardCheck,
                title: "90-day fix roadmap",
                desc: "Prioritized: schema markup, content gaps, citation sources, GBP fixes, review-language coaching. No fluff, no 'phase 2 we'll discuss later.' Real next steps.",
              },
              {
                icon: PhoneCall,
                title: "60-minute walkthrough call",
                desc: "Live with me. We go through the report together, I answer every question, and you leave knowing exactly what to do — whether you hire us, do it in-house, or keep your current agency.",
              },
            ].map((item) => (
              <div
                key={item.title}
                className="rounded-xl border border-border bg-card p-6 shadow-sm transition-smooth hover:shadow-elegant"
              >
                <div className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-lg bg-brand-gold/15 text-brand-gold-deep">
                  <item.icon className="h-5 w-5" />
                </div>
                <h3 className="text-xl font-bold text-foreground">{item.title}</h3>
                <p className="mt-2 text-muted-foreground">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing tiers */}
      <section className="bg-background py-20">
        <div className="mx-auto max-w-6xl px-5 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <p className="text-sm font-semibold uppercase tracking-wide text-brand-gold-deep">Two ways to work with me</p>
            <h2 className="mt-2 text-3xl font-bold text-foreground sm:text-4xl">Pick the one that fits where you are right now</h2>
          </div>

          <div className="mt-12 grid gap-8 md:grid-cols-2">
            {/* Tier 1 */}
            <div className="flex flex-col rounded-2xl border border-border bg-card p-8 shadow-sm">
              <p className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">One-time</p>
              <h3 className="mt-1 text-2xl font-bold text-foreground">AI Visibility Audit</h3>
              <p className="mt-3 text-4xl font-bold text-foreground">
                $750<span className="text-base font-normal text-muted-foreground"> one-time</span>
              </p>
              <p className="mt-3 text-muted-foreground">
                Best for: owners who want a clear, honest snapshot of where they stand and a fix list they can hand to their team or current agency.
              </p>
              <ul className="mt-6 space-y-3 text-foreground">
                {[
                  "25+ live prompts across ChatGPT, Perplexity, Gemini, AI Overviews",
                  "Citation gap report (who's beating you and why)",
                  "90-day fix roadmap, prioritized",
                  "60-minute live walkthrough with Chris",
                  "PDF report + Loom walkthrough you keep",
                ].map((b) => (
                  <li key={b} className="flex gap-2">
                    <CheckCircle2 className="mt-0.5 h-5 w-5 flex-none text-brand-gold-deep" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
              <Link
                to="/contact"
                search={{ topic: "ai-audit" }}
                className="mt-8 inline-flex items-center justify-center gap-2 rounded-full border border-brand-gold-deep px-6 py-3 font-semibold text-brand-gold-deep transition-smooth hover:bg-brand-gold/10"
              >
                Book the audit <ArrowRight className="h-4 w-4" />
              </Link>
            </div>

            {/* Tier 2 — featured */}
            <div className="relative flex flex-col rounded-2xl border-2 border-brand-gold-deep bg-card p-8 shadow-elegant">
              <span className="absolute -top-3 left-8 rounded-full bg-brand-gold-deep px-3 py-1 text-xs font-bold uppercase tracking-wide text-brand-black">
                Most popular
              </span>
              <p className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">90-day engagement</p>
              <h3 className="mt-1 text-2xl font-bold text-foreground">Coach + Audit</h3>
              <p className="mt-3 text-4xl font-bold text-foreground">
                $1,850<span className="text-base font-normal text-muted-foreground"> for 90 days</span>
              </p>
              <p className="mt-3 text-muted-foreground">
                Best for: owners who want a trusted Colorado guide walking them through AI search, GEO, and marketing decisions — not a vendor who hands them a report and disappears.
              </p>
              <ul className="mt-6 space-y-3 text-foreground">
                {[
                  "Everything in the audit",
                  "Three monthly 1:1 strategy calls with Chris",
                  "Slack / email access between calls",
                  "Quarterly re-audit so we measure progress",
                  "Direct help making vendor & in-house decisions",
                ].map((b) => (
                  <li key={b} className="flex gap-2">
                    <CheckCircle2 className="mt-0.5 h-5 w-5 flex-none text-brand-gold-deep" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
              <Link
                to="/contact"
                search={{ topic: "ai-coach" }}
                className="mt-8 inline-flex items-center justify-center gap-2 rounded-full bg-brand-gold-deep px-6 py-3 font-semibold text-brand-black shadow-elegant transition-smooth hover:bg-brand-gold"
              >
                Start coaching + audit <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>

          <p className="mx-auto mt-8 max-w-2xl text-center text-sm text-muted-foreground">
            Already a managed-services client? The audit is included free annually. Just{" "}
            <Link to="/contact" className="font-semibold text-brand-gold-deep underline hover:no-underline">
              ask Chris
            </Link>
            .
          </p>
        </div>
      </section>

      {/* Who it's for / not for */}
      <section className="border-t border-border bg-section-alt py-20">
        <div className="mx-auto max-w-5xl px-5 lg:px-8">
          <div className="grid gap-10 md:grid-cols-2">
            <div>
              <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-brand-gold/15 px-3 py-1 text-sm font-semibold text-brand-gold-deep">
                <CheckCircle2 className="h-4 w-4" /> This is for you if…
              </div>
              <h3 className="text-2xl font-bold text-foreground">You actually want answers, not a sales funnel</h3>
              <ul className="mt-5 space-y-3 text-foreground">
                <li className="flex gap-2"><CheckCircle2 className="mt-1 h-4 w-4 flex-none text-brand-gold-deep" /> You're a Colorado small business doing $250K–$10M+ in revenue.</li>
                <li className="flex gap-2"><CheckCircle2 className="mt-1 h-4 w-4 flex-none text-brand-gold-deep" /> You've heard "AI SEO," "GEO," "AEO" thrown around and you don't know what's real.</li>
                <li className="flex gap-2"><CheckCircle2 className="mt-1 h-4 w-4 flex-none text-brand-gold-deep" /> Your traffic looks "fine" but leads have been quietly slipping for 6+ months.</li>
                <li className="flex gap-2"><CheckCircle2 className="mt-1 h-4 w-4 flex-none text-brand-gold-deep" /> You want a real human who'll tell you what to ignore, not just what to buy.</li>
              </ul>
            </div>
            <div>
              <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-destructive/10 px-3 py-1 text-sm font-semibold text-destructive">
                <AlertTriangle className="h-4 w-4" /> This is NOT for you if…
              </div>
              <h3 className="text-2xl font-bold text-foreground">You want guarantees nobody can honestly make</h3>
              <ul className="mt-5 space-y-3 text-muted-foreground">
                <li className="flex gap-2"><AlertTriangle className="mt-1 h-4 w-4 flex-none text-destructive" /> You want "guaranteed ChatGPT placement." That's not a real product. Anyone selling it is lying.</li>
                <li className="flex gap-2"><AlertTriangle className="mt-1 h-4 w-4 flex-none text-destructive" /> You're hoping a $750 audit replaces a long-term marketing strategy. It won't.</li>
                <li className="flex gap-2"><AlertTriangle className="mt-1 h-4 w-4 flex-none text-destructive" /> You're not based in or serving Colorado. My local-market judgment is half the value.</li>
                <li className="flex gap-2"><AlertTriangle className="mt-1 h-4 w-4 flex-none text-destructive" /> You want fluff. I'll be the most direct person you've talked to in 5 years.</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Why me */}
      <section className="bg-background py-20">
        <div className="mx-auto max-w-4xl px-5 lg:px-8 text-center">
          <p className="text-sm font-semibold uppercase tracking-wide text-brand-gold-deep">Why me</p>
          <h2 className="mt-2 text-3xl font-bold text-foreground sm:text-4xl">
            25+ years in Colorado. AI didn't catch me off guard — it caught up to what we were already doing.
          </h2>
          <div className="mt-8 grid gap-6 sm:grid-cols-3 text-left">
            {[
              { icon: MapPin, title: "Local market, real relationships", desc: "I know what a Colorado Springs HVAC owner is panicked about in January. AI doesn't." },
              { icon: ShieldCheck, title: "No vendor stack, no upsell", desc: "If your fix is free and takes you an hour, I'll tell you. The audit is the audit." },
              { icon: Compass, title: "Translator, not jargon-pusher", desc: "AEO, GEO, AIO, SGE — I'll explain which actually matters for you and which you can safely ignore." },
            ].map((b) => (
              <div key={b.title} className="rounded-xl border border-border bg-card p-6">
                <div className="mb-3 inline-flex h-10 w-10 items-center justify-center rounded-lg bg-brand-gold/15 text-brand-gold-deep">
                  <b.icon className="h-5 w-5" />
                </div>
                <h3 className="font-bold text-foreground">{b.title}</h3>
                <p className="mt-1 text-sm text-muted-foreground">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-border bg-section-alt py-20">
        <div className="mx-auto max-w-3xl px-5 lg:px-8">
          <h2 className="text-3xl font-bold text-foreground sm:text-4xl">Common questions</h2>
          <Accordion type="single" collapsible className="mt-8">
            {FAQS.map((f, i) => (
              <AccordionItem key={f.q} value={`faq-${i}`}>
                <AccordionTrigger className="text-left text-lg font-semibold">{f.q}</AccordionTrigger>
                <AccordionContent className="text-base leading-relaxed text-muted-foreground">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-gradient-dark py-20 text-brand-cream">
        <div className="mx-auto max-w-3xl px-5 text-center lg:px-8">
          <Bot className="mx-auto mb-4 h-10 w-10 text-brand-gold" />
          <h2 className="text-3xl font-bold sm:text-4xl">Find out where you actually stand in AI search.</h2>
          <p className="mt-4 text-lg text-brand-cream/80">
            Seven days. Real prompts. A real plan. Booked directly with Chris — not a sales rep.
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Link
              to="/contact"
              search={{ topic: "ai-audit" }}
              className="inline-flex items-center gap-2 rounded-full bg-brand-gold px-7 py-3 text-base font-semibold text-brand-black shadow-elegant transition-smooth hover:bg-brand-gold-deep"
            >
              Book my audit <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/geo-ai"
              className="inline-flex items-center gap-2 rounded-full border border-brand-cream/30 px-7 py-3 text-base font-semibold text-brand-cream transition-smooth hover:border-brand-gold hover:text-brand-gold"
            >
              See the done-for-you GEO service <Sparkles className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
